import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import SEO from "@/components/seo/SEO";
import api from "@/lib/api";
import { stripMarkdown } from "@/lib/markdown";

const markdownComponents = {
  h1: ({ children }) => <h2 className="font-heading text-xl sm:text-2xl font-bold mt-8 mb-3 text-[#2B2B2B]">{children}</h2>,
  h2: ({ children }) => <h2 className="font-heading text-xl sm:text-2xl font-bold mt-8 mb-3 text-[#2B2B2B]">{children}</h2>,
  h3: ({ children }) => <h3 className="font-heading text-lg font-bold mt-6 mb-2 text-[#2B2B2B]">{children}</h3>,
  p: ({ children }) => <p className="text-sm text-[#2B2B2B]/80 leading-relaxed mb-4">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 text-sm text-[#2B2B2B]/80 leading-relaxed mb-4 space-y-1.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 text-sm text-[#2B2B2B]/80 leading-relaxed mb-4 space-y-1.5">{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#2B2B2B]">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  a: ({ children, href }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#4ba3b7] hover:underline">{children}</a>,
};

export default function BlogPostPage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const lang = i18n.language?.slice(0, 2) || "fr";

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/blog/${slug}`, { params: { lang } }).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
  }, [slug, lang]);

  if (notFound) {
    return <p className="text-center py-24 text-[#2B2B2B]/50" data-testid="blog-post-not-found">{t("search.noResults")}</p>;
  }

  if (!post) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#4ba3b7]" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid="blog-post-page">
      <SEO title={`${post.title} | JobVinum`} description={stripMarkdown(post.content).slice(0, 160)} image="/blog-og-image.png" path={`/blog/${slug}`} />
      <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[#4ba3b7] mb-6 hover:underline" data-testid="blog-post-back-link">
        <ArrowLeft className="h-4 w-4" /> {t("blog.backToList")}
      </Link>
      <div className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-6 sm:p-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-6">{post.title}</h1>
        <div data-testid="blog-post-content">
          <ReactMarkdown remarkPlugins={[remarkBreaks]} components={markdownComponents}>{post.content}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
